import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import type { ColorGrade, ColorWheel as Wheel } from '@cinegrid/core';
import {
  defaultColorGrade,
  defaultColorWheel,
  resolveColorGrade,
} from '@cinegrid/core';
import { useEditorStore } from '../../state/editorStore';
import { PanelShell, SectionLabel, SliderRow } from '../controls/PanelShell';

type ColorTab = 'primaries' | 'qualifier' | 'hdr' | 'raw' | 'colorspace';

const TABS: { id: ColorTab; label: string }[] = [
  { id: 'primaries', label: 'Primaries' },
  { id: 'qualifier', label: 'Qualifier' },
  { id: 'hdr', label: 'HDR' },
  { id: 'raw', label: 'Camera Raw' },
  { id: 'colorspace', label: 'Color Space' },
];

function ColorWheelControl({
  label,
  wheel,
  onChange,
  size = 88,
}: {
  label: string;
  wheel: Wheel;
  onChange: (w: Partial<Wheel>) => void;
  size?: number;
}) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const cx = size / 2;
    const cy = size / 2;
    const r = size / 2 - 3;
    ctx.clearRect(0, 0, size, size);
    for (let a = 0; a < 360; a += 2) {
      const rad = (a * Math.PI) / 180;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, rad, rad + (2.5 * Math.PI) / 180);
      ctx.closePath();
      ctx.fillStyle = `hsl(${a}, 72%, 46%)`;
      ctx.fill();
    }
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.32, 0, Math.PI * 2);
    ctx.fillStyle =
      getComputedStyle(document.documentElement).getPropertyValue('--cg-bg-raised').trim() ||
      '#282c31';
    ctx.fill();

    const ix = cx + wheel.r * r * 0.7 - wheel.b * r * 0.35;
    const iy = cy - wheel.g * r * 0.7;
    ctx.beginPath();
    ctx.arc(ix, iy, 3.5, 0, Math.PI * 2);
    ctx.fillStyle = '#fff';
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1;
    ctx.fill();
    ctx.stroke();
  }, [wheel.r, wheel.g, wheel.b, size]);

  const onPointer = (e: ReactPointerEvent) => {
    const canvas = ref.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
    const mag = Math.min(1, Math.hypot(x, y));
    const nx = (x / (mag || 1)) * mag;
    const ny = (y / (mag || 1)) * mag;
    onChange({
      r: Math.max(-1, Math.min(1, nx * 0.8 + ny * 0.2)),
      g: Math.max(-1, Math.min(1, ny)),
      b: Math.max(-1, Math.min(1, -nx * 0.8 + ny * 0.2)),
    });
  };

  const reset = () => onChange(defaultColorWheel());

  return (
    <div className="flex min-w-0 flex-col items-center gap-1">
      <div className="flex w-full items-center justify-between px-0.5">
        <span className="text-2xs font-medium text-text-high">{label}</span>
        <button
          type="button"
          title={`Reset ${label}`}
          className="text-[10px] text-text-low hover:text-accent"
          onClick={reset}
        >
          ↺
        </button>
      </div>
      <canvas
        ref={ref}
        style={{ width: size, height: size }}
        className="cursor-crosshair"
        onPointerDown={(e) => {
          (e.target as HTMLElement).setPointerCapture(e.pointerId);
          onPointer(e);
        }}
        onPointerMove={(e) => {
          if (e.buttons) onPointer(e);
        }}
        onDoubleClick={(e) => {
          e.preventDefault();
          reset();
        }}
      />
      <div className="grid w-full grid-cols-4 gap-0.5 text-[9px] text-text-low">
        {(['r', 'g', 'b', 'master'] as const).map((k) => (
          <input
            key={k}
            type="text"
            inputMode="decimal"
            value={Number(wheel[k].toFixed(2))}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isFinite(n)) onChange({ [k]: n });
            }}
            className="w-full border border-hairline bg-base px-0.5 py-0.5 text-center text-text-high outline-none focus:border-accent"
            title={k}
          />
        ))}
      </div>
      <input
        type="range"
        min={-1}
        max={1}
        step={0.01}
        value={wheel.master}
        onChange={(e) => onChange({ master: Number(e.target.value) })}
        className="h-1 w-full accent-[var(--cg-accent)]"
        title="Master"
      />
    </div>
  );
}

function RangeBar({
  label,
  enabled,
  onEnabled,
  gradient,
  low,
  high,
  onLow,
  onHigh,
  softLow,
  softHigh,
  onSoftLow,
  onSoftHigh,
}: {
  label: string;
  enabled: boolean;
  onEnabled: (v: boolean) => void;
  gradient: string;
  low: number;
  high: number;
  onLow: (v: number) => void;
  onHigh: (v: number) => void;
  softLow: number;
  softHigh: number;
  onSoftLow: (v: number) => void;
  onSoftHigh: (v: number) => void;
}) {
  return (
    <div className="mb-2 border-b border-hairline px-2 pb-2">
      <div className="mb-1 flex items-center gap-2">
        <input type="checkbox" checked={enabled} onChange={(e) => onEnabled(e.target.checked)} />
        <span className="text-2xs text-text-high">{label}</span>
      </div>
      <div
        className="relative mb-1 h-3 w-full border border-hairline"
        style={{ background: gradient }}
      >
        <input
          type="range"
          min={0}
          max={1}
          step={0.001}
          value={low}
          onChange={(e) => onLow(Math.min(Number(e.target.value), high - 0.01))}
          className="absolute inset-0 w-full opacity-70 accent-[var(--cg-accent)]"
        />
        <input
          type="range"
          min={0}
          max={1}
          step={0.001}
          value={high}
          onChange={(e) => onHigh(Math.max(Number(e.target.value), low + 0.01))}
          className="absolute inset-0 w-full opacity-70 accent-[var(--cg-accent)]"
        />
      </div>
      <div className="grid grid-cols-4 gap-1">
        {[
          { l: 'Low', v: low, f: onLow },
          { l: 'High', v: high, f: onHigh },
          { l: 'L Soft', v: softLow, f: onSoftLow },
          { l: 'H Soft', v: softHigh, f: onSoftHigh },
        ].map((f) => (
          <label key={f.l} className="text-[9px] text-text-low">
            {f.l}
            <input
              type="text"
              inputMode="decimal"
              value={Number(f.v.toFixed(2))}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (Number.isFinite(n)) f.f(n);
              }}
              className="mt-0.5 w-full border border-hairline bg-base px-1 py-0.5 text-2xs text-text-high outline-none focus:border-accent"
            />
          </label>
        ))}
      </div>
    </div>
  );
}

function SelectRow({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-2 px-2 py-1 text-2xs">
      <span className="shrink-0 text-text-low">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-w-0 flex-1 border border-hairline bg-base px-1.5 py-1 text-text-high outline-none focus:border-accent"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

export function ColorPanel() {
  const selection = useEditorStore((s) => s.selection);
  const project = useEditorStore((s) => s.project);
  const playhead = useEditorStore((s) => s.playhead);
  const updateClipColor = useEditorStore((s) => s.updateClipColor);
  const selectClips = useEditorStore((s) => s.selectClips);
  const [tab, setTab] = useState<ColorTab>('primaries');

  const clip = useMemo(() => {
    const selected = selection
      .map((id) => project.clips.find((c) => c.id === id))
      .find((c) => c != null);
    if (selected) return selected;
    const videoTrackIds = new Set(
      project.tracks.filter((t) => t.kind === 'video').map((t) => t.id)
    );
    const under = project.clips.filter(
      (c) =>
        videoTrackIds.has(c.trackId) &&
        playhead >= c.start &&
        playhead < c.start + c.duration
    );
    return under[under.length - 1];
  }, [project.clips, project.tracks, selection, playhead]);

  const grade: ColorGrade = resolveColorGrade(clip?.properties.color);
  const defaults = defaultColorGrade();

  if (!clip) {
    return (
      <PanelShell title="Color">
        <p className="p-3 text-2xs text-text-low">
          Select a clip on the timeline (or park the playhead on one) to grade —
          Primaries, Qualifier, HDR, Camera Raw, Color Space.
        </p>
      </PanelShell>
    );
  }

  const patch = (p: Partial<ColorGrade>) => {
    if (!selection.includes(clip.id)) selectClips([clip.id]);
    updateClipColor(clip.id, p);
  };

  return (
    <PanelShell
      title="Color"
      actions={
        <button
          type="button"
          className="text-2xs text-text-low hover:text-accent"
          onClick={() => updateClipColor(clip.id, defaultColorGrade())}
        >
          Reset
        </button>
      }
      bodyClassName="flex min-h-0 flex-col overflow-hidden"
    >
      <div className="flex shrink-0 border-b border-hairline">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`flex-1 px-1 py-1.5 text-[10px] ${
              tab === t.id
                ? 'border-b-2 border-accent text-accent'
                : 'text-text-low hover:text-text-high'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {tab === 'primaries' && (
          <>
            <SectionLabel>Primaries · Color Wheels</SectionLabel>
            <div className="grid grid-cols-2 gap-2 border-b border-hairline p-2 sm:grid-cols-4">
              <ColorWheelControl
                label="Lift"
                wheel={grade.lift}
                onChange={(w) => patch({ lift: { ...grade.lift, ...w } })}
              />
              <ColorWheelControl
                label="Gamma"
                wheel={grade.gamma}
                onChange={(w) => patch({ gamma: { ...grade.gamma, ...w } })}
              />
              <ColorWheelControl
                label="Gain"
                wheel={grade.gain}
                onChange={(w) => patch({ gain: { ...grade.gain, ...w } })}
              />
              <ColorWheelControl
                label="Offset"
                wheel={grade.offset}
                onChange={(w) => patch({ offset: { ...grade.offset, ...w } })}
              />
            </div>
            <SectionLabel>Balance</SectionLabel>
            <SliderRow label="Temp" value={grade.temperature} defaultValue={defaults.temperature} onChange={(temperature) => patch({ temperature })} />
            <SliderRow label="Tint" value={grade.tint} defaultValue={defaults.tint} onChange={(tint) => patch({ tint })} />
            <SliderRow label="Contrast" value={grade.contrast} min={0} max={2} defaultValue={defaults.contrast} onChange={(contrast) => patch({ contrast })} />
            <SliderRow label="Pivot" value={grade.pivots} min={0} max={1} defaultValue={defaults.pivots} onChange={(pivots) => patch({ pivots })} />
            <SliderRow label="Mid/Detail" value={grade.midDetail} defaultValue={defaults.midDetail} onChange={(midDetail) => patch({ midDetail })} />
            <SectionLabel>Tonal</SectionLabel>
            <SliderRow label="Exposure" value={grade.exposure} min={-3} max={3} defaultValue={defaults.exposure} onChange={(exposure) => patch({ exposure })} />
            <SliderRow label="Shadows" value={grade.shadows} defaultValue={defaults.shadows} onChange={(shadows) => patch({ shadows })} />
            <SliderRow label="Midtones" value={grade.midtones} defaultValue={defaults.midtones} onChange={(midtones) => patch({ midtones })} />
            <SliderRow label="Highlights" value={grade.highlights} defaultValue={defaults.highlights} onChange={(highlights) => patch({ highlights })} />
            <SliderRow label="Whites" value={grade.whites} defaultValue={defaults.whites} onChange={(whites) => patch({ whites })} />
            <SliderRow label="Blacks" value={grade.blacks} defaultValue={defaults.blacks} onChange={(blacks) => patch({ blacks })} />
            <SectionLabel>Color</SectionLabel>
            <SliderRow label="Color Boost" value={grade.colorBoost} min={0} max={1} defaultValue={defaults.colorBoost} onChange={(colorBoost) => patch({ colorBoost })} />
            <SliderRow label="Saturation" value={grade.saturation} min={0} max={2} defaultValue={defaults.saturation} onChange={(saturation) => patch({ saturation })} />
            <SliderRow label="Hue" value={grade.hue} min={-180} max={180} step={1} defaultValue={defaults.hue} onChange={(hue) => patch({ hue })} format={(v) => `${v | 0}°`} />
            <SliderRow label="Lum Mix" value={grade.lumMix} min={0} max={1} defaultValue={defaults.lumMix} onChange={(lumMix) => patch({ lumMix })} />
            <SliderRow label="Intensity" value={grade.intensity} min={0} max={1} defaultValue={defaults.intensity} onChange={(intensity) => patch({ intensity })} />
          </>
        )}

        {tab === 'qualifier' && (
          <>
            <SectionLabel>Qualifier · HSL</SectionLabel>
            <div className="flex items-center gap-2 px-2 py-1.5">
              <label className="flex items-center gap-1.5 text-2xs text-text-low">
                <input
                  type="checkbox"
                  checked={grade.qualifier.enabled}
                  onChange={(e) =>
                    patch({ qualifier: { ...grade.qualifier, enabled: e.target.checked } })
                  }
                />
                Enable
              </label>
              <input
                type="color"
                value={grade.qualifier.pickHex}
                title="Pick color"
                onChange={(e) => {
                  const hex = e.target.value;
                  const r = parseInt(hex.slice(1, 3), 16) / 255;
                  const g = parseInt(hex.slice(3, 5), 16) / 255;
                  const b = parseInt(hex.slice(5, 7), 16) / 255;
                  const max = Math.max(r, g, b);
                  const min = Math.min(r, g, b);
                  let h = 0;
                  const d = max - min;
                  if (d) {
                    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
                    else if (max === g) h = ((b - r) / d + 2) * 60;
                    else h = ((r - g) / d + 4) * 60;
                  }
                  const soft = 25;
                  patch({
                    qualifier: {
                      ...grade.qualifier,
                      pickHex: hex,
                      enabled: true,
                      hueMin: (h - soft + 360) % 360,
                      hueMax: (h + soft) % 360,
                    },
                  });
                }}
                className="h-6 w-8 cursor-pointer border border-hairline bg-transparent"
              />
              <label className="flex items-center gap-1.5 text-2xs text-text-low">
                <input
                  type="checkbox"
                  checked={grade.qualifier.invert}
                  onChange={(e) =>
                    patch({ qualifier: { ...grade.qualifier, invert: e.target.checked } })
                  }
                />
                Invert
              </label>
            </div>
            <RangeBar
              label="Hue"
              enabled={grade.qualifier.enabled}
              onEnabled={(enabled) => patch({ qualifier: { ...grade.qualifier, enabled } })}
              gradient="linear-gradient(90deg,#f00,#ff0,#0f0,#0ff,#00f,#f0f,#f00)"
              low={grade.qualifier.hueMin / 360}
              high={grade.qualifier.hueMax / 360}
              onLow={(v) => patch({ qualifier: { ...grade.qualifier, hueMin: v * 360 } })}
              onHigh={(v) => patch({ qualifier: { ...grade.qualifier, hueMax: v * 360 } })}
              softLow={grade.qualifier.hueSoft}
              softHigh={grade.qualifier.hueSoft}
              onSoftLow={(hueSoft) => patch({ qualifier: { ...grade.qualifier, hueSoft } })}
              onSoftHigh={(hueSoft) => patch({ qualifier: { ...grade.qualifier, hueSoft } })}
            />
            <RangeBar
              label="Saturation"
              enabled={grade.qualifier.enabled}
              onEnabled={(enabled) => patch({ qualifier: { ...grade.qualifier, enabled } })}
              gradient="linear-gradient(90deg,#888,hsl(210,100%,50%))"
              low={grade.qualifier.satMin}
              high={grade.qualifier.satMax}
              onLow={(satMin) => patch({ qualifier: { ...grade.qualifier, satMin } })}
              onHigh={(satMax) => patch({ qualifier: { ...grade.qualifier, satMax } })}
              softLow={grade.qualifier.satSoftLow}
              softHigh={grade.qualifier.satSoftHigh}
              onSoftLow={(satSoftLow) => patch({ qualifier: { ...grade.qualifier, satSoftLow } })}
              onSoftHigh={(satSoftHigh) => patch({ qualifier: { ...grade.qualifier, satSoftHigh } })}
            />
            <RangeBar
              label="Luminance"
              enabled={grade.qualifier.enabled}
              onEnabled={(enabled) => patch({ qualifier: { ...grade.qualifier, enabled } })}
              gradient="linear-gradient(90deg,#000,#fff)"
              low={grade.qualifier.lumMin}
              high={grade.qualifier.lumMax}
              onLow={(lumMin) => patch({ qualifier: { ...grade.qualifier, lumMin } })}
              onHigh={(lumMax) => patch({ qualifier: { ...grade.qualifier, lumMax } })}
              softLow={grade.qualifier.lumSoftLow}
              softHigh={grade.qualifier.lumSoftHigh}
              onSoftLow={(lumSoftLow) => patch({ qualifier: { ...grade.qualifier, lumSoftLow } })}
              onSoftHigh={(lumSoftHigh) => patch({ qualifier: { ...grade.qualifier, lumSoftHigh } })}
            />
            <SectionLabel>Matte Finesse</SectionLabel>
            <SliderRow
              label="Soft"
              value={grade.qualifier.soft}
              min={0}
              max={1}
              defaultValue={defaults.qualifier.soft}
              onChange={(soft) => patch({ qualifier: { ...grade.qualifier, soft } })}
            />
            <SliderRow
              label="Blur Radius"
              value={grade.qualifier.blurRadius}
              min={0}
              max={1}
              defaultValue={0}
              onChange={(blurRadius) => patch({ qualifier: { ...grade.qualifier, blurRadius } })}
            />
            <SliderRow
              label="Clean Black"
              value={grade.qualifier.cleanBlack}
              min={0}
              max={1}
              defaultValue={0}
              onChange={(cleanBlack) => patch({ qualifier: { ...grade.qualifier, cleanBlack } })}
            />
            <SliderRow
              label="Clean White"
              value={grade.qualifier.cleanWhite}
              min={0}
              max={1}
              defaultValue={0}
              onChange={(cleanWhite) => patch({ qualifier: { ...grade.qualifier, cleanWhite } })}
            />
          </>
        )}

        {tab === 'hdr' && (
          <>
            <SectionLabel>HDR · Color Wheels</SectionLabel>
            <div className="grid grid-cols-2 gap-2 border-b border-hairline p-2 sm:grid-cols-4">
              {(
                [
                  { key: 'dark', label: 'Dark', exp: 'darkExp', sat: 'darkSat' },
                  { key: 'shadow', label: 'Shadow', exp: 'shadowExp', sat: 'shadowSat' },
                  { key: 'light', label: 'Light', exp: 'lightExp', sat: 'lightSat' },
                  { key: 'global', label: 'Global', exp: 'globalExp', sat: 'globalSat' },
                ] as const
              ).map(({ key, label, exp, sat }) => (
                <div key={key} className="flex flex-col gap-1">
                  <ColorWheelControl
                    label={label}
                    wheel={grade.hdr[key]}
                    onChange={(w) =>
                      patch({
                        hdr: {
                          ...grade.hdr,
                          [key]: { ...grade.hdr[key], ...w },
                        },
                      })
                    }
                    size={80}
                  />
                  <SliderRow
                    label="Exp"
                    value={grade.hdr[exp]}
                    min={-2}
                    max={2}
                    defaultValue={0}
                    onChange={(v) => patch({ hdr: { ...grade.hdr, [exp]: v } })}
                  />
                  <SliderRow
                    label="Sat"
                    value={grade.hdr[sat]}
                    min={0}
                    max={2}
                    defaultValue={1}
                    onChange={(v) => patch({ hdr: { ...grade.hdr, [sat]: v } })}
                  />
                </div>
              ))}
            </div>
            <p className="px-2 py-1.5 text-[10px] text-text-low">
              HDR zone wheels bake into the live grade (Dark/Shadow → lift, Light → gain, Global →
              offset + exposure/sat).
            </p>
            <SectionLabel>Global</SectionLabel>
            <SliderRow label="Temp" value={grade.temperature} defaultValue={0} onChange={(temperature) => patch({ temperature })} />
            <SliderRow label="Tint" value={grade.tint} defaultValue={0} onChange={(tint) => patch({ tint })} />
            <SliderRow label="Hue" value={grade.hue} min={-180} max={180} step={1} defaultValue={0} onChange={(hue) => patch({ hue })} format={(v) => `${v | 0}°`} />
            <SliderRow label="Contrast" value={grade.contrast} min={0} max={2} defaultValue={1} onChange={(contrast) => patch({ contrast })} />
            <SliderRow label="Pivot" value={grade.pivots} min={0} max={1} defaultValue={0.5} onChange={(pivots) => patch({ pivots })} />
          </>
        )}

        {tab === 'raw' && (
          <>
            <SectionLabel>Camera Raw</SectionLabel>
            <SelectRow
              label="Decode Quality"
              value={grade.cameraRaw.decodeQuality}
              options={['Use Project Setting', 'Full Res', 'Half Res', 'Quarter Res']}
              onChange={(decodeQuality) =>
                patch({ cameraRaw: { ...grade.cameraRaw, decodeQuality } })
              }
            />
            <SelectRow
              label="Decode Using"
              value={grade.cameraRaw.decodeUsing}
              options={['Clip', 'Project']}
              onChange={(decodeUsing) =>
                patch({ cameraRaw: { ...grade.cameraRaw, decodeUsing } })
              }
            />
            <SelectRow
              label="Color Science"
              value={grade.cameraRaw.colorScience}
              options={['Gen 5', 'Gen 4', 'Gen 3']}
              onChange={(colorScience) =>
                patch({ cameraRaw: { ...grade.cameraRaw, colorScience } })
              }
            />
            <SelectRow
              label="White Balance"
              value={grade.cameraRaw.whiteBalance}
              options={['As Shot', 'Custom', 'Daylight', 'Tungsten']}
              onChange={(whiteBalance) =>
                patch({ cameraRaw: { ...grade.cameraRaw, whiteBalance } })
              }
            />
            <SelectRow
              label="Color Space"
              value={grade.cameraRaw.colorSpace}
              options={['Blackmagic Design', 'Rec.709', 'Rec.2020', 'ACES AP0']}
              onChange={(colorSpace) =>
                patch({ cameraRaw: { ...grade.cameraRaw, colorSpace } })
              }
            />
            <SelectRow
              label="Gamma"
              value={grade.cameraRaw.gamma}
              options={[
                'Blackmagic Design Custom',
                'Blackmagic Design Film',
                'Rec.709',
                'Linear',
              ]}
              onChange={(gamma) => patch({ cameraRaw: { ...grade.cameraRaw, gamma } })}
            />
            <SliderRow
              label="ISO"
              value={grade.cameraRaw.iso}
              min={100}
              max={12800}
              step={50}
              defaultValue={800}
              onChange={(iso) => patch({ cameraRaw: { ...grade.cameraRaw, iso } })}
              format={(v) => String(Math.round(v))}
            />
            <label className="flex items-center gap-2 px-2 py-1.5 text-2xs text-text-low">
              <input
                type="checkbox"
                checked={grade.cameraRaw.highlightRecovery}
                onChange={(e) =>
                  patch({
                    cameraRaw: { ...grade.cameraRaw, highlightRecovery: e.target.checked },
                  })
                }
              />
              Highlight Recovery
            </label>
            <SectionLabel>Basic</SectionLabel>
            <SliderRow
              label="Color Temp"
              value={grade.cameraRaw.colorTemp}
              min={2000}
              max={10000}
              step={50}
              defaultValue={5600}
              onChange={(colorTemp) => {
                const temperature = (colorTemp - 5600) / 2000;
                patch({
                  cameraRaw: { ...grade.cameraRaw, colorTemp },
                  temperature,
                });
              }}
              format={(v) => String(Math.round(v))}
            />
            <SliderRow
              label="Tint"
              value={grade.cameraRaw.tint}
              min={-100}
              max={100}
              step={1}
              defaultValue={10}
              onChange={(tintRaw) => {
                patch({
                  cameraRaw: { ...grade.cameraRaw, tint: tintRaw },
                  tint: tintRaw / 100,
                });
              }}
            />
            <SliderRow
              label="Exposure"
              value={grade.cameraRaw.exposure}
              min={-3}
              max={3}
              defaultValue={0}
              onChange={(exposure) => {
                patch({
                  cameraRaw: { ...grade.cameraRaw, exposure },
                  exposure,
                });
              }}
            />
            <SectionLabel>Gamma Controls</SectionLabel>
            <SliderRow
              label="Saturation"
              value={grade.cameraRaw.saturation}
              min={0}
              max={2}
              defaultValue={1}
              onChange={(saturation) => {
                patch({
                  cameraRaw: { ...grade.cameraRaw, saturation },
                  saturation,
                });
              }}
            />
            <SliderRow
              label="Contrast"
              value={grade.cameraRaw.contrast}
              min={0}
              max={2}
              defaultValue={1}
              onChange={(contrast) => {
                patch({
                  cameraRaw: { ...grade.cameraRaw, contrast },
                  contrast,
                });
              }}
            />
            <SliderRow
              label="Midpoint"
              value={grade.cameraRaw.midpoint}
              min={0}
              max={1}
              defaultValue={0.41}
              onChange={(midpoint) => {
                patch({
                  cameraRaw: { ...grade.cameraRaw, midpoint },
                  pivots: midpoint,
                });
              }}
            />
            <SliderRow
              label="Highlight Rolloff"
              value={grade.cameraRaw.highlightRolloff}
              min={0}
              max={2}
              defaultValue={1}
              onChange={(highlightRolloff) =>
                patch({ cameraRaw: { ...grade.cameraRaw, highlightRolloff } })
              }
            />
            <SliderRow
              label="Shadow Rolloff"
              value={grade.cameraRaw.shadowRolloff}
              min={0}
              max={2}
              defaultValue={1}
              onChange={(shadowRolloff) =>
                patch({ cameraRaw: { ...grade.cameraRaw, shadowRolloff } })
              }
            />
            <SliderRow
              label="White Level"
              value={grade.cameraRaw.whiteLevel}
              min={0}
              max={2}
              defaultValue={1}
              onChange={(whiteLevel) =>
                patch({ cameraRaw: { ...grade.cameraRaw, whiteLevel } })
              }
            />
            <SliderRow
              label="Black Level"
              value={grade.cameraRaw.blackLevel}
              min={0}
              max={1}
              defaultValue={0}
              onChange={(blackLevel) =>
                patch({ cameraRaw: { ...grade.cameraRaw, blackLevel } })
              }
            />
            <label className="flex items-center gap-2 px-2 py-1.5 text-2xs text-text-low">
              <input
                type="checkbox"
                checked={grade.cameraRaw.useVideoBlackLevel}
                onChange={(e) =>
                  patch({
                    cameraRaw: {
                      ...grade.cameraRaw,
                      useVideoBlackLevel: e.target.checked,
                    },
                  })
                }
              />
              Use Video Black Level
            </label>
          </>
        )}

        {tab === 'colorspace' && (
          <>
            <SectionLabel>Color Space & Transforms</SectionLabel>
            <SelectRow
              label="Color science"
              value={grade.colorSpace.colorScience}
              options={[
                'DaVinci YRGB Color Managed',
                'DaVinci YRGB',
                'ACEScct',
              ]}
              onChange={(colorScience) =>
                patch({ colorSpace: { ...grade.colorSpace, colorScience } })
              }
            />
            <SelectRow
              label="Color processing mode"
              value={grade.colorSpace.processingMode}
              options={['Custom', 'HDR', 'SDR']}
              onChange={(processingMode) =>
                patch({ colorSpace: { ...grade.colorSpace, processingMode } })
              }
            />
            <SelectRow
              label="Input color space"
              value={grade.colorSpace.inputColorSpace}
              options={[
                'Blackmagic Design Film Gen 5',
                'Rec.709',
                'Rec.2020',
                'ACES AP0',
                'sRGB',
              ]}
              onChange={(inputColorSpace) =>
                patch({ colorSpace: { ...grade.colorSpace, inputColorSpace } })
              }
            />
            <SelectRow
              label="Timeline color space"
              value={grade.colorSpace.timelineColorSpace}
              options={[
                'DaVinci WG/Intermediate',
                'Rec.709',
                'Rec.2020',
                'ACEScg',
              ]}
              onChange={(timelineColorSpace) =>
                patch({ colorSpace: { ...grade.colorSpace, timelineColorSpace } })
              }
            />
            <SelectRow
              label="Timeline working luminance"
              value={grade.colorSpace.timelineLuminance}
              options={['HDR 1000', 'HDR 4000', 'SDR 100']}
              onChange={(timelineLuminance) =>
                patch({ colorSpace: { ...grade.colorSpace, timelineLuminance } })
              }
            />
            <SelectRow
              label="Output color space"
              value={grade.colorSpace.outputColorSpace}
              options={['Rec.709 (Scene)', 'Rec.709', 'Rec.2020', 'P3-D65']}
              onChange={(outputColorSpace) =>
                patch({ colorSpace: { ...grade.colorSpace, outputColorSpace } })
              }
            />
            <SelectRow
              label="Limit output gamut to"
              value={grade.colorSpace.limitOutputGamut}
              options={['Output color space', 'None', 'Rec.709']}
              onChange={(limitOutputGamut) =>
                patch({ colorSpace: { ...grade.colorSpace, limitOutputGamut } })
              }
            />
            <SelectRow
              label="Input DRT"
              value={grade.colorSpace.inputDrt}
              options={['DaVinci', 'None', 'ACES']}
              onChange={(inputDrt) =>
                patch({ colorSpace: { ...grade.colorSpace, inputDrt } })
              }
            />
            <SelectRow
              label="Output DRT"
              value={grade.colorSpace.outputDrt}
              options={['DaVinci', 'None', 'ACES']}
              onChange={(outputDrt) =>
                patch({ colorSpace: { ...grade.colorSpace, outputDrt } })
              }
            />
            <SelectRow
              label="Apply resize transformations in"
              value={grade.colorSpace.applyResizeIn}
              options={['Gamma', 'Linear']}
              onChange={(applyResizeIn) =>
                patch({ colorSpace: { ...grade.colorSpace, applyResizeIn } })
              }
            />
            {(
              [
                ['automaticColorManagement', 'Automatic color management'],
                ['separateColorSpaceGamma', 'Use separate color space and gamma'],
                ['inverseDrtSdrHdr', 'Use inverse DRT for SDR to HDR conversion'],
                ['whitePointAdaptation', 'Use white point adaptation'],
                ['colorSpaceAwareTools', 'Use color space aware grading tools'],
                ['disableToneMappingFusion', 'Disable tone mapping for Fusion conversions'],
              ] as const
            ).map(([key, label]) => (
              <label
                key={key}
                className="flex items-center gap-2 px-2 py-1 text-2xs text-text-low"
              >
                <input
                  type="checkbox"
                  checked={Boolean(grade.colorSpace[key])}
                  onChange={(e) =>
                    patch({
                      colorSpace: { ...grade.colorSpace, [key]: e.target.checked },
                    })
                  }
                />
                {label}
              </label>
            ))}
            <SliderRow
              label="Graphics white level"
              value={grade.colorSpace.graphicsWhiteLevel}
              min={1}
              max={10000}
              step={1}
              defaultValue={100}
              onChange={(graphicsWhiteLevel) =>
                patch({ colorSpace: { ...grade.colorSpace, graphicsWhiteLevel } })
              }
              format={(v) => `${Math.round(v)} nits`}
            />
          </>
        )}
      </div>
    </PanelShell>
  );
}
